import React, {useCallback, useEffect, useState} from "react";
import {Header} from "../UserList/UserList.styled";
import {InputLabel, MenuItem, OutlinedInput, Select, Typography} from "@mui/material";
import {useNavigate, useParams} from "react-router-dom";
import {useSnackbar} from "../../../contexts/SnackbarContext";
import BackButton from "../../../components/common/BackButton";
import FieldHelpSuffix from "../../../components/common/FieldHelpSuffix";
import {ActionButton} from "../../../components/common/ActionButtons";
import {parseError} from "../../../errors/http.service";
import {useTranslation} from 'react-i18next';
import {validatePassword, validatePasswordMatch} from "../../../utils/password-validation";
import {StyledContainer} from "../../workspace/UserDataSourceList.styled";
import {useUser} from "../../../contexts/UserContext";
import {normalizePhoneNumber, phoneInputProps, validatePhoneNumber} from "../../../utils/phone-validation";
import {
    ButtonGroup,
    FormGrid,
    StyledFormControl,
    StyledTextField,
    UserEditForm,
    UserEditWrapper,
} from "./UserEdit.styled";
import {usersService} from "../../../services/users.service";
import {UpdateUserRequest, User, USER_ROLES} from "../../../models/users.models";
import CircularProgress from "@mui/material/CircularProgress";

const UserEdit = () => {
    const navigate = useNavigate();
    const {showSnackbar} = useSnackbar();
    const {t} = useTranslation();

    const {id} = useParams();
    const numericId = id ? Number(id) : undefined;

    const {user: loggedUser, fetchUserData} = useUser();

    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState<User | null>(null);

    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [phoneNumberTouched, setPhoneNumberTouched] = useState(false);
    const [passwordTouched, setPasswordTouched] = useState(false);
    const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [resendingWelcome, setResendingWelcome] = useState<boolean>(false);

    const sanitizeQueryParam = (val: string | null): string => {
        if (val === null) return "";
        const lowered = val.trim().toLowerCase();
        return lowered === "null" || lowered === "undefined" ? "" : val;
    };

    const nullIfEmpty = (val: string | undefined | null) => {
        if (val === undefined || val === null) return null;
        return val.toString().trim() === "" ? null : val;
    };

    const fetchUserDetails = useCallback(async () => {
        setIsLoading(true);

        if (!numericId) {
            showSnackbar(t('userEdit.message.fetchUserDataNoIdError'), "error");
            setIsLoading(false);
            return;
        }

        try {
            const userData = await usersService.fetchUserData(numericId);

            setUser(userData);

            setUser({
                id: userData.id,
                email: userData.email,
                firstName: sanitizeQueryParam(userData.firstName),
                lastName: sanitizeQueryParam(userData.lastName),
                createdAt: userData.createdAt,
                role: userData.role as USER_ROLES,
                activated: userData.activated,
                position: sanitizeQueryParam(userData.position),
                phoneNumber: sanitizeQueryParam(userData.phoneNumber),
                isAdUser: userData.isAdUser,
            });
        } catch (error) {
            console.error("Error fetching user details:", error);
            showSnackbar(t('userEdit.message.fetchUserDataError'), "error");
        } finally {
            setIsLoading(false);
        }
    }, [t, numericId]);

    useEffect(() => {
        void fetchUserDetails();
    }, [numericId, t]);

    const handleInputChange = (field: keyof User, value: any) => {
        setUser((prev) => prev && {...prev, [field]: value});
    };

    const passwordValidationError = password ? validatePassword(password) : null;
    const phoneNumberValidationError = validatePhoneNumber(user?.phoneNumber || "");
    const confirmPasswordValidationError = !password && !confirmPassword
        ? null
        : validatePasswordMatch(password, confirmPassword);

    const showPhoneNumberError = (phoneNumberTouched || submitted) && !!phoneNumberValidationError;
    const showPasswordError = (passwordTouched || submitted) && !!passwordValidationError;
    const showConfirmPasswordError = (confirmPasswordTouched || submitted) && !!confirmPasswordValidationError;

    const handleSubmit = async () => {
        setSubmitted(true);

        if (!user) {
            showSnackbar(t('userEdit.message.fetchUserDataError'), "error");
            setSubmitted(false);
            return;
        }

        try {
            if (phoneNumberValidationError || passwordValidationError || confirmPasswordValidationError) return;

            const updateData: UpdateUserRequest = {};

            if (password && !user?.isAdUser) {
                updateData.newPassword = password;
            }

            updateData.firstName = user?.firstName ?? "";
            updateData.lastName = user?.lastName ?? "";
            updateData.role = user?.role ?? null;
            updateData.isActive = user?.activated ?? null;
            updateData.position = nullIfEmpty(user?.position);
            updateData.phoneNumber = nullIfEmpty(user?.phoneNumber)

            await usersService.updateUserData(user.email, updateData);

            showSnackbar(t('userEdit.message.updateSuccess'), "success");

            if (loggedUser?.id === user?.id && loggedUser?.role !== user?.role) {
                await fetchUserData();
                navigate("/");
            } else {
                navigate(-1);
            }
        } catch (error) {
            showSnackbar(parseError(error), "error");
        }
    };

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        void handleSubmit();
    };

    const handleResendWelcomeEmail = async (userId: number | undefined) => {
        setResendingWelcome(true);

        if (!userId) {
            showSnackbar('Pojawił się błąd podczas ponownego wysyłania e-maila powitalnego - brak ID użytkownika', 'error');
            setResendingWelcome(false);
            return;
        }

        try {
            await usersService.sendWelcomeEmail(userId);
            showSnackbar(t('userEdit.welcomeEmailResent'), 'success');
        } catch (error) {
            showSnackbar(parseError(error), 'error');
            console.error('Error resending welcome email:', error);
        } finally {
            setResendingWelcome(false);
        }
    };

    if (!user) {
        return null;
    }

    return (
        <StyledContainer role="region" aria-labelledby="user-edit-title">
            <Header>
                <h2 id="user-edit-title">{t('userEdit.title')}</h2>
            </Header>

            {isLoading && (
                <UserEditWrapper>
                    <CircularProgress />
                </UserEditWrapper>
            )}

            {!isLoading && (
                <UserEditWrapper>
                    <UserEditForm onSubmit={handleFormSubmit}>
                        <FormGrid>
                            <StyledTextField
                                label={t('userEdit.usernameLabel')}
                                value={user.email}
                                disabled
                                fullWidth
                                variant="outlined"
                                inputProps={{ 'aria-label': String(t('userEdit.usernameLabel')) }}
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('createUser.help.email')}
                                            ariaLabel={t('createUser.help.email') as string}
                                        />
                                    ),
                                }}
                            />
                            <StyledTextField
                                label={t('userEdit.createdDateLabel')}
                                value={new Date(user.createdAt).toLocaleString('pl-PL', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: false,
                                    timeZone: 'Europe/Warsaw',
                                })}
                                disabled
                                fullWidth
                                variant="outlined"
                                inputProps={{ 'aria-label': String(t('userEdit.createdDateLabel')) }}
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('userEdit.help.createdAt')}
                                            ariaLabel={t('userEdit.help.createdAt') as string}
                                        />
                                    ),
                                }}
                            />
                            <StyledTextField
                                label={t('userEdit.firstNameLabel')}
                                value={user.firstName || ""}
                                onChange={(e) => handleInputChange("firstName", e.target.value)}
                                fullWidth
                                variant="outlined"
                                inputProps={{ 'aria-label': String(t('userEdit.firstNameLabel')) }}
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('createUser.help.firstName')}
                                            ariaLabel={t('createUser.help.firstName') as string}
                                        />
                                    ),
                                }}
                            />
                            <StyledTextField
                                label={t('userEdit.lastNameLabel')}
                                value={user.lastName || ""}
                                onChange={(e) => handleInputChange("lastName", e.target.value)}
                                fullWidth
                                variant="outlined"
                                inputProps={{ 'aria-label': String(t('userEdit.lastNameLabel')) }}
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('createUser.help.lastName')}
                                            ariaLabel={t('createUser.help.lastName') as string}
                                        />
                                    ),
                                }}
                            />
                            <StyledFormControl fullWidth variant="outlined">
                                <InputLabel>{t('userEdit.roleLabel')}</InputLabel>
                                <Select
                                    value={user.role}
                                    onChange={(e) => handleInputChange("role", e.target.value)}
                                    label={t('userEdit.roleLabel')}
                                    sx={{
                                        '& .MuiSelect-icon': {
                                            right: 40,
                                        },
                                    }}
                                    input={
                                        <OutlinedInput
                                            label={t('userEdit.roleLabel')}
                                            inputProps={{ 'aria-label': String(t('userEdit.roleLabel')) }}
                                            endAdornment={
                                                <FieldHelpSuffix
                                                    asAdornment
                                                    title={t('userEdit.help.role')}
                                                    ariaLabel={t('userEdit.help.role') as string}
                                                />
                                            }
                                            sx={{ pr: '72px' }}
                                        />
                                    }
                                >
                                    <MenuItem value={USER_ROLES.SYSTEM_ADMIN}>
                                        {t('userEdit.roleOptions.systemAdministrator')}
                                    </MenuItem>
                                    <MenuItem value={USER_ROLES.DOCUMENTS_ADMIN}>
                                        {t('userEdit.roleOptions.documentsAdministrator')}
                                    </MenuItem>
                                    <MenuItem value={USER_ROLES.USER}>
                                        {t('userEdit.roleOptions.user')}
                                    </MenuItem>
                                </Select>
                            </StyledFormControl>
                            <StyledFormControl fullWidth variant="outlined">
                                <InputLabel>{t('userEdit.statusLabel')}</InputLabel>
                                <Select
                                    value={user.activated.toString()}
                                    onChange={(e) =>
                                        handleInputChange("activated", e.target.value === "true")
                                    }
                                    label={t('userEdit.statusLabel')}
                                    sx={{
                                        '& .MuiSelect-icon': {
                                            right: 40,
                                        },
                                    }}
                                    input={
                                        <OutlinedInput
                                            label={t('userEdit.statusLabel')}
                                            inputProps={{ 'aria-label': String(t('userEdit.statusLabel')) }}
                                            endAdornment={
                                                <FieldHelpSuffix
                                                    asAdornment
                                                    title={t('createUser.help.status')}
                                                    ariaLabel={t('createUser.help.status') as string}
                                                />
                                            }
                                            sx={{ pr: '72px' }}
                                        />
                                    }
                                >
                                    <MenuItem value="true">{t('userEdit.statusOptions.active')}</MenuItem>
                                    <MenuItem value="false">{t('userEdit.statusOptions.inactive')}</MenuItem>
                                </Select>
                            </StyledFormControl>

                            <StyledTextField
                                label={t('userEdit.positionLabel')}
                                value={user.position || ""}
                                onChange={(e) => handleInputChange("position", e.target.value)}
                                fullWidth
                                variant="outlined"
                                inputProps={{ 'aria-label': String(t('userEdit.positionLabel')) }}
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('createUser.help.position')}
                                            ariaLabel={t('createUser.help.position') as string}
                                        />
                                    ),
                                }}
                            />
                            <StyledTextField
                                label={t('userEdit.phoneNumberLabel')}
                                type="tel"
                                inputProps={{
                                    ...phoneInputProps,
                                    'aria-label': String(t('userEdit.phoneNumberLabel')),
                                }}
                                value={user.phoneNumber || ""}
                                error={showPhoneNumberError}
                                helperText={showPhoneNumberError ? String(t(phoneNumberValidationError!.key)) : ""}
                                onChange={(e) => handleInputChange("phoneNumber", normalizePhoneNumber(e.target.value))}
                                onBlur={() => setPhoneNumberTouched(true)}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('createUser.help.phoneNumber')}
                                            ariaLabel={t('createUser.help.phoneNumber') as string}
                                        />
                                    ),
                                }}
                            />

                            {user.isAdUser ? (
                                <Typography variant="body1" color="text.hint" sx={{ pt: 2 }}>
                                    {t('userEdit.externalAccountInfo')}
                                </Typography>
                            )
                            : (
                                <>
                                    <StyledTextField
                                        label={t('userEdit.newPasswordLabel')}
                                        type="password"
                                        autoComplete="new-password"
                                        value={password}
                                        onChange={(e) => {
                                            setPassword(e.target.value);
                                        }}
                                        onBlur={() => setPasswordTouched(true)}
                                        fullWidth
                                        variant="outlined"
                                        error={showPasswordError}
                                        helperText={showPasswordError ? String(t(passwordValidationError!.key)) : ""}
                                        inputProps={{ 'aria-label': String(t('userEdit.newPasswordLabel')) }}
                                        InputProps={{
                                            endAdornment: (
                                                <FieldHelpSuffix
                                                    asAdornment
                                                    title={t('createUser.help.password')}
                                                    ariaLabel={t('createUser.help.password') as string}
                                                />
                                            ),
                                        }}
                                    />
                                    <StyledTextField
                                        label={t('createUser.repeatPassword')}
                                        type="password"
                                        autoComplete="new-password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        onBlur={() => setConfirmPasswordTouched(true)}
                                        fullWidth
                                        variant="outlined"
                                        error={showConfirmPasswordError}
                                        helperText={showConfirmPasswordError ? String(t(confirmPasswordValidationError!.key)) : ""}
                                        inputProps={{ 'aria-label': String(t('createUser.repeatPassword')) }}
                                        InputProps={{
                                            endAdornment: (
                                                <FieldHelpSuffix
                                                    asAdornment
                                                    title={t('createUser.help.repeatPassword')}
                                                    ariaLabel={t('createUser.help.repeatPassword') as string}
                                                />
                                            ),
                                        }}
                                    />
                                </>
                            )}
                        </FormGrid>

                        <ButtonGroup>
                            <BackButton text={t('userEdit.cancelButton') as string}/>

                            <ActionButton
                                type="button"
                                variant="outlined"
                                onClick={() => handleResendWelcomeEmail(Number(user?.id))}
                                disabled={resendingWelcome || !(user?.id) || user.isAdUser}
                            >
                                {resendingWelcome ? t('userEdit.resendWelcome.sending') : t('userEdit.resendWelcome.button')}
                            </ActionButton>

                            <ActionButton type="submit" disabled={!!phoneNumberValidationError}>
                                {t('userEdit.saveButton')}
                            </ActionButton>
                        </ButtonGroup>
                    </UserEditForm>
                </UserEditWrapper>
            )}
        </StyledContainer>
    )
        ;
};

export default UserEdit;
